import React, { useState, useEffect, useRef } from 'react';
import { animate, stagger } from 'animejs';
import supabase from '../supabase/supabase';
import { formatPHP, formatDate } from '../utils/format';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import EditExpenseModal from '../components/expenses/EditExpenseModal';
import DeleteExpenseModal from '../components/expenses/DeleteExpenseModal';

const { FiPlus, FiShoppingBag, FiCalendar, FiDollarSign, FiEdit2, FiTrash2, FiMessageSquare, FiChevronDown, FiChevronUp } = FiIcons;
const TABLE_NAME = 'expenses_1739880000000';

const Expenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [editingExpense, setEditingExpense] = useState(null);
  const [deletingExpense, setDeletingExpense] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const tableRef = useRef(null);
  const cardsRef = useRef(null);
  const msgRef = useRef(null);
  const formRef = useRef(null);

  const [formData, setFormData] = useState({
    supplier: '', purchase_date: new Date().toISOString().split('T')[0], amount: '', remarks: ''
  });

  useEffect(() => { fetchExpenses(); }, []);

  useEffect(() => {
    if (!loading) {
      if (tableRef.current) {
        animate(tableRef.current.querySelectorAll('.expense-row'), {
          opacity: [0, 1], translateY: [15, 0],
          delay: stagger(40), duration: 350, ease: 'outExpo',
        });
      }
      if (cardsRef.current) {
        animate(cardsRef.current.querySelectorAll('.expense-card'), {
          opacity: [0, 1], translateY: [20, 0],
          delay: stagger(50), duration: 400, ease: 'outExpo',
        });
      }
    }
  }, [loading, expenses]);

  useEffect(() => {
    if (message.text && msgRef.current) {
      animate(msgRef.current, { opacity: [0, 1], translateY: [-12, 0], duration: 300, ease: 'outExpo' });
    }
  }, [message.text]);

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.from(TABLE_NAME).select('*')
        .order('purchase_date', { ascending: false }).order('created_at', { ascending: false }).limit(100);
      if (error) throw error;
      setExpenses(data || []);
    } catch (error) {
      console.error('Error fetching expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (expense) => {
    const row = tableRef.current?.querySelector(`[data-id="${expense.id}"]`);
    const card = cardsRef.current?.querySelector(`[data-id="${expense.id}"]`);
    const target = row || card;
    if (target) {
      animate(target, {
        translateX: [-6, 6, -4, 4, 0], duration: 300, ease: 'linear',
        onComplete: () => setDeletingExpense(expense),
      });
    } else {
      setDeletingExpense(expense);
    }
  };

  const toggleForm = () => {
    if (!showForm) {
      setShowForm(true);
      setTimeout(() => {
        if (formRef.current) {
          animate(formRef.current, { opacity: [0, 1], translateY: [-10, 0], duration: 300, ease: 'outExpo' });
        }
      }, 10);
    } else {
      if (formRef.current) {
        animate(formRef.current, {
          opacity: [1, 0], translateY: [0, -10], duration: 200, ease: 'inExpo',
          onComplete: () => setShowForm(false),
        });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage({ type: '', text: '' });
    try {
      const { error } = await supabase.from(TABLE_NAME).insert([{
        supplier: formData.supplier, purchase_date: formData.purchase_date,
        amount: parseFloat(formData.amount), remarks: formData.remarks
      }]);
      if (error) throw error;
      setMessage({ type: 'success', text: 'Expense added successfully!' });
      setFormData({ supplier: '', purchase_date: new Date().toISOString().split('T')[0], amount: '', remarks: '' });
      fetchExpenses();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Expenses</h1>
        <button
          onClick={toggleForm}
          className="flex items-center space-x-2 px-4 py-2.5 bg-emerald-600 text-white font-bold rounded-xl hover:bg-emerald-700 transition-colors text-sm shadow-sm"
        >
          <SafeIcon icon={FiPlus} />
          <span>Add New</span>
          <SafeIcon icon={showForm ? FiChevronUp : FiChevronDown} className="text-xs" />
        </button>
      </header>

      {/* Add Expense Form */}
      {showForm && (
        <div ref={formRef} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 md:p-6" style={{ opacity: 0 }}>
          <h2 className="text-base font-bold text-gray-900 mb-4 flex items-center">
            <SafeIcon icon={FiPlus} className="mr-2 text-emerald-600" />Add New Expense
          </h2>

          {message.text && (
            <div ref={msgRef} className={`mb-4 p-3 rounded-xl text-sm opacity-0 ${message.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Supplier</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <SafeIcon icon={FiShoppingBag} className="text-gray-400 text-sm" />
                  </div>
                  <input type="text" required
                    className="block w-full pl-9 pr-3 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="e.g. SM Supermarket" value={formData.supplier}
                    onChange={(e) => setFormData({ ...formData, supplier: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <SafeIcon icon={FiCalendar} className="text-gray-400 text-sm" />
                  </div>
                  <input type="date" required
                    className="block w-full pl-9 pr-3 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    value={formData.purchase_date}
                    onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₱)</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <SafeIcon icon={FiDollarSign} className="text-gray-400 text-sm" />
                  </div>
                  <input type="number" step="0.01" min="0" required
                    className="block w-full pl-9 pr-3 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="0.00" value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })} />
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
              <div className="relative">
                <div className="absolute top-3 left-3 pointer-events-none">
                  <SafeIcon icon={FiMessageSquare} className="text-gray-400 text-sm" />
                </div>
                <textarea rows="2"
                  className="block w-full pl-9 pr-3 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="Optional notes..." value={formData.remarks}
                  onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}></textarea>
              </div>
            </div>
            <div className="flex gap-3 pt-1">
              <button type="submit" disabled={isSubmitting}
                className="flex-1 md:flex-none md:px-10 py-3 bg-emerald-600 text-white font-bold rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-70 text-sm">
                {isSubmitting ? 'Saving...' : 'Save Expense'}
              </button>
              <button type="button" onClick={toggleForm}
                className="flex-1 md:flex-none md:px-6 py-3 bg-gray-100 text-gray-600 font-medium rounded-xl hover:bg-gray-200 transition-colors text-sm">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Success/error message when form is hidden */}
      {message.text && !showForm && (
        <div ref={msgRef} className={`p-3 rounded-xl text-sm opacity-0 ${message.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
          {message.text}
        </div>
      )}

      {/* Expense History */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-4 md:p-6 border-b border-gray-100 flex justify-between items-center">
          <h2 className="text-base md:text-lg font-bold text-gray-900">Expense History</h2>
          <span className="text-xs text-gray-400 bg-gray-50 px-2 py-1 rounded-lg">{expenses.length} records</span>
        </div>

        {loading ? (
          <div className="p-10 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
          </div>
        ) : expenses.length === 0 ? (
          <div className="p-10 text-center text-gray-400">
            <SafeIcon icon={FiShoppingBag} className="text-3xl mx-auto mb-2 opacity-30" />
            <p className="text-sm">No expenses found.</p>
          </div>
        ) : (
          <>
            {/* Mobile: Card View */}
            <div ref={cardsRef} className="md:hidden divide-y divide-gray-50">
              {expenses.map((expense) => (
                <div key={expense.id} data-id={expense.id}
                  className="expense-card opacity-0 p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0 pr-3">
                      <p className="font-bold text-gray-900 text-sm truncate">{expense.supplier}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{formatDate(expense.purchase_date)}</p>
                      {expense.remarks && (
                        <p className="text-xs text-gray-500 mt-1.5 bg-gray-50 px-2 py-1 rounded-lg italic truncate">
                          {expense.remarks}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col items-end space-y-2 flex-shrink-0">
                      <span className="font-bold text-gray-900 text-sm">{formatPHP(expense.amount)}</span>
                      <div className="flex space-x-1">
                        <button onClick={() => setEditingExpense(expense)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                          <SafeIcon icon={FiEdit2} className="text-xs" />
                        </button>
                        <button onClick={() => handleDelete(expense)}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                          <SafeIcon icon={FiTrash2} className="text-xs" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop: Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider border-b border-gray-100">
                    <th className="p-4 font-bold">Date</th>
                    <th className="p-4 font-bold">Supplier & Remarks</th>
                    <th className="p-4 font-bold text-right">Amount</th>
                    <th className="p-4 font-bold text-center">Actions</th>
                  </tr>
                </thead>
                <tbody ref={tableRef} className="divide-y divide-gray-100">
                  {expenses.map((expense) => (
                    <tr key={expense.id} data-id={expense.id}
                      className="expense-row opacity-0 hover:bg-gray-50 transition-colors group">
                      <td className="p-4 text-gray-600 font-medium whitespace-nowrap align-top">{formatDate(expense.purchase_date)}</td>
                      <td className="p-4 align-top">
                        <p className="text-gray-900 font-bold">{expense.supplier}</p>
                        {expense.remarks && <p className="text-xs text-gray-500 mt-0.5 italic">"{expense.remarks}"</p>}
                      </td>
                      <td className="p-4 font-bold text-gray-900 text-right align-top">{formatPHP(expense.amount)}</td>
                      <td className="p-4 align-top">
                        <div className="flex items-center justify-center space-x-2">
                          <button onClick={() => setEditingExpense(expense)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                            <SafeIcon icon={FiEdit2} />
                          </button>
                          <button onClick={() => handleDelete(expense)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                            <SafeIcon icon={FiTrash2} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {editingExpense && <EditExpenseModal expense={editingExpense} onClose={() => setEditingExpense(null)} onSuccess={() => { setEditingExpense(null); fetchExpenses(); }} />}
      {deletingExpense && <DeleteExpenseModal expense={deletingExpense} onClose={() => setDeletingExpense(null)} onSuccess={() => { setDeletingExpense(null); fetchExpenses(); }} />}
    </div>
  );
};

export default Expenses;
